// src/hooks/useFixActionsData.ts
import { useEffect, useMemo, useState } from "react";
import { fixActionsService } from "../../services/fix-actions/fix-actions-service";
import type { FixAction, ContentTemplate } from "../../services/fix-actions/fix-actions-service";

type PriorityCard = {
  id: string;
  headline: string;
  description: string;
  priority: "High" | "Medium" | "Strategic";
  stageId: string;
};

function safePriorityOptimizations(cognitionData: any): PriorityCard[] {
  const direct = cognitionData?.priorityOptimizations;
  if (!Array.isArray(direct)) return [];

  return direct
    .map((x: any, idx: number) => ({
      id: String(x.id ?? idx),
      headline: String(x.headline ?? x.title ?? "").trim(),
      description: String(x.description ?? "").trim(),
      priority: (x.priority ?? "Medium") as any,
      stageId: String(x.stageId ?? "evidence-signals"),
    }))
    .filter((x: any) => x.headline);
}

export interface FixActionsData {
  fixActions: FixAction[];
  actionLog: FixAction[];
  antennaIngested: FixAction[];
  cognitionIngested: FixAction[];
  contentTemplates: ContentTemplate[];
  generatedContent: any[];
}

function byCreatedDesc(a: FixAction, b: FixAction): number {
  const ta = new Date(a.createdAt || 0).getTime();
  const tb = new Date(b.createdAt || 0).getTime();
  return tb - ta;
}

function byImpactDesc(a: FixAction, b: FixAction): number {
  const ia = Number(a.impactScore ?? 0);
  const ib = Number(b.impactScore ?? 0);
  return ib - ia;
}

export function useFixActionsData(args: {
  brandName: string;
  selectedModels: string[];
  antennaData: any | null;
  cognitionData: any | null;
  brandId: string;
}) {
  const { brandName, selectedModels, antennaData, cognitionData, brandId } = args;

  const [data, setData] = useState<FixActionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const monitoringRecommendedActions = useMemo(
    () =>
      Array.isArray(antennaData?.recommendedActions) ? antennaData.recommendedActions : [],
    [antennaData]
  );

  const cognitionPriorityOptimizations = useMemo(
    () => safePriorityOptimizations(cognitionData),
    [cognitionData]
  );

  useEffect(() => {
    let mounted = true;

    async function buildStateFromPersisted(persisted: any, suggested: any) {
      const actionLog = Array.isArray(persisted?.actionLog)
        ? (persisted.actionLog as FixAction[])
        : [];

      const antennaIngested = actionLog
        .filter((a) => String(a.sourceModule || "").toLowerCase() === "antenna")
        .sort(byCreatedDesc);

      const cognitionIngested = actionLog
        .filter((a) => String(a.sourceModule || "").toLowerCase() === "cognition")
        .sort(byCreatedDesc);

      const plannerActions = (Array.isArray(suggested?.fixActions)
        ? suggested.fixActions
        : []
      )
        .slice()
        .sort(byImpactDesc);

      return {
        fixActions: plannerActions,
        actionLog,
        antennaIngested,
        cognitionIngested,
        contentTemplates: Array.isArray(persisted?.contentTemplates)
          ? persisted.contentTemplates
          : [],
        generatedContent: [],
      } as FixActionsData;
    }

    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Kick off initial persisted + suggested fetches in parallel
        const [persisted, suggested] = await Promise.all([
          fixActionsService.getFixActionsPageData({ brandId }),
          fixActionsService.buildSuggestedActions({
            brandName,
            selectedModels,
            monitoringRecommendedActions,
            cognitionPriorityOptimizations,
            brandId,
          }),
        ]);

        if (!mounted) return;

        // First paint — whatever we have
        const firstPass = await buildStateFromPersisted(persisted, suggested);
        setData(firstPass);

        // ── Backfill-on-demand ─────────────────────────────────
        // Fire-and-forget: ask the backend to enrich any ingested Antenna/
        // Cognition rows missing detail. If it enriched any, re-fetch just the
        // action log and swap in the updated ingested rows. Suggested list
        // (planner output) is untouched so we don't rebuild the LLM plan.
        const needsBackfillCheck =
          (firstPass.antennaIngested.length > 0 &&
            firstPass.antennaIngested.some(
              (r) => !r.artifacts?.length || !(r.observation || "").trim()
            )) ||
          (firstPass.cognitionIngested.length > 0 &&
            firstPass.cognitionIngested.some(
              (r) => !r.artifacts?.length || !(r.observation || "").trim()
            ));

        if (needsBackfillCheck) {
          try {
            const result = await fixActionsService.backfillEnrichment({
              brandId,
              brandName,
              selectedModels,
              limit: 20,
            });

            if (mounted && result?.enrichedCount > 0) {
              // Re-fetch only the action log; keep existing planner suggested
              const refreshed = await fixActionsService.getFixActionsPageData({
                brandId,
              });
              if (!mounted) return;

              const next = await buildStateFromPersisted(refreshed, suggested);
              setData(next);

              // Let the rest of the app know the data moved
              window.dispatchEvent(new Event("fix_actions_refresh"));
            }
          } catch (e) {
            // Non-fatal — user sees first-pass view; may re-load to retry
            console.warn("backfill-enrichment failed (non-fatal):", e);
          }
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e instanceof Error ? e : new Error("Failed to load fix actions data"));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [
    brandName,
    brandId,
    JSON.stringify(selectedModels),
    JSON.stringify(monitoringRecommendedActions),
    JSON.stringify(cognitionPriorityOptimizations),
  ]);

  return { data, loading, error };
}